import { Outlet } from "react-router-dom";

// Global UI
import GlobalBar from "./components/globalBar/globalBar";
import Sidebar from "./components/leftsidebar/leftSideBar";
import MobileBottomBar from "./components/leftsidebar/MobileBottomBar";

/**
 * AppLayout
 *
 * Layout contract:
 * - Exactly ONE scroll container (the <main> element)
 * - Persistent UI (top bar, mobile bottom bar) must reserve space
 * - No scrolling on <body> or <html>
 * - All flex parents that contain scrollable children MUST use min-h-0
 *
 * Breakpoint strategy:
 * - Mobile / portrait tablet (<768px)  → bottom bar, no sidebar
 * - Landscape tablet / desktop (≥768px) → sidebar, no bottom bar
 * - md = 768px is the single source of truth across ALL layout files
 */
const AppLayout = () => {
  return (
    <div className="h-screen flex flex-col bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-white overflow-hidden">

      {/* =====================================================
          TOP GLOBAL BAR
          - Fixed height (64px)
          - Outside scroll container
         ===================================================== */}
      <GlobalBar />

      {/* =====================================================
          MAIN FLEX ROW
          - Fills remaining viewport height
          - min-h-0 is CRITICAL to prevent flex overflow
          - pt-16 reserves space for GlobalBar: its <nav> is
            `position: fixed`, so it occupies zero space in this
            flex column no matter where it sits in the JSX tree --
            without this padding, both the sidebar and page content
            started at y=0 and rendered underneath the fixed bar.
         ===================================================== */}
      <div className="flex flex-1 min-h-0 pt-16">

        {/* =================================================
            DESKTOP SIDEBAR
            - Hidden below 768px (md)
            - Sticky, does not scroll
            - top-0 here (not top-16): the row above already
              shifted everything down by 64px via pt-16, so
              stacking another 64px offset here would leave a
              second gap under the bar.
           ================================================= */}
        <aside className="hidden md:block sticky top-0 h-[calc(100vh-64px)] flex-shrink-0">
          <Sidebar />
        </aside>

        {/* =================================================
            PAGE CONTENT — SINGLE SCROLL CONTAINER
            - flex-1 fills all remaining horizontal space
            - min-h-0 prevents flex children overflowing
            - overflow-y-auto is the ONE scroll authority
            - pb-16 on mobile reserves space for bottom bar
            - md:pb-0 removes that padding on desktop
           ================================================= */}
        {/* tabIndex=0: this is the app's one scroll container (see note
            above); axe's scrollable-region-focusable rule requires a
            scrollable region to be reachable by keyboard even when its
            current page content happens to have no focusable elements of
            its own (found on /leaderboard, whose rows are plain display
            text, not links/buttons). */}
        <main tabIndex={0} className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pb-16 md:pb-0">
          <Outlet />
        </main>

      </div>

      {/* =====================================================
          MOBILE BOTTOM BAR
          - Visible below 768px (md) only
          - Part of the flex column layout (not fixed/absolute)
          - Sticky bottom-0 keeps it anchored to viewport bottom
          - h-16 matches the pb-16 padding on <main>
         ===================================================== */}
      <div className="md:hidden sticky bottom-0 z-[9999] h-16 bg-white border-t border-gray-200 dark:bg-gray-900 dark:border-slate-800">
        <MobileBottomBar />
      </div>

    </div>
  );
};

export default AppLayout;